import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2"; // Import SweetAlert2
import Navbar from "components/Navbars/AuthNavbar";
import Footer from "components/Footers/Footer";
import BlockUiOverlay from "../Expense/BlockUiOverlay";
import BASE_URL from "../BaseUrl/apiConfig";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

function EmailServerSettings() {
  const [formValues, setFormValues] = useState({
    smtpHost: "",
    smtpPort: "",
    smtpUsername: "",
    smtpPassword: "",
    senderEmail: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [emailData, setEmailData] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // Track if in edit mode
  const [currentSettingID, setCurrentSettingID] = useState(null); // Track the current SettingID for editing
  const history = useHistory();

  const fetchEmailData = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      const response = await axios.post(
        `${BASE_URL}/api/Expense/GetAllEmailSMTP?GroupID=${userData.groupId}`
      );
      setEmailData(response.data);
    } catch (error) {
      console.error("Error fetching email data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "There was an error fetching the email data. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailData();
  }, []);

  const validate = () => {
    const errors = {};
    if (!formValues.smtpHost) errors.smtpHost = "SMTP Host is required";
    if (!formValues.smtpPort) errors.smtpPort = "SMTP Port is required";
    if (!formValues.smtpUsername)
      errors.smtpUsername = "SMTP Username is required";
    if (!formValues.smtpPassword)
      errors.smtpPassword = "SMTP Password is required";
    if (!formValues.senderEmail) {
      errors.senderEmail = "Sender Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.senderEmail)) {
      errors.senderEmail = "Enter a valid email address";
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setErrors(errors);
    if (Object.keys(errors).length !== 0) return;

    setLoading(true);
    const userData = JSON.parse(localStorage.getItem("userData"));
    const data = {
      userID: userData.userId,
      groupID: userData.groupId,
      smtpHost: formValues.smtpHost,
      smtpPort: parseInt(formValues.smtpPort, 10),
      smtpUsername: formValues.smtpUsername,
      smtpPassword: formValues.smtpPassword,
      senderEmail: formValues.senderEmail,
      settingID: currentSettingID, // Include SettingID if editing
    };

    try {
      const response = await axios.post(
        `${BASE_URL}/api/Expense/AddUpdateEmailSettingsForSMTP`,
        data
      );

      Swal.fire({
        icon: "success",
        title: isEditing
          ? "SMTP Settings Updated Successfully."
          : "SMTP Settings Saved Successfully.",
      });
      setIsEditing(false);
      setCurrentSettingID(null);
      setFormValues({
        smtpHost: "",
        smtpPort: "",
        smtpUsername: "",
        smtpPassword: "",
        senderEmail: "",
      });
      setIsEditMode(false); // Ensure we switch back to save mode
      fetchEmailData(); // Refresh data after update
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "There was an error submitting the form. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleEditSMTP = async (settingID) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/Expense/GetSMTPDetailsByID?SettingID=${settingID}`
      );
      const smtpDetails = response.data;

      setFormValues({
        smtpHost: smtpDetails.smtpHost,
        smtpPort: smtpDetails.smtpPort,
        smtpUsername: smtpDetails.smtpUsername,
        smtpPassword: smtpDetails.smtpPassword,
        senderEmail: smtpDetails.senderEmail,
      });

      setCurrentSettingID(settingID); // Set current SettingID
      setIsEditMode(true); // Switch to edit mode
    } catch (error) {
      console.error("Error fetching SMTP details:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "There was an error fetching the SMTP details. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormValues({
      smtpHost: "",
      smtpPort: "",
      smtpUsername: "",
      smtpPassword: "",
      senderEmail: "",
    });
    setIsEditMode(false); // Switch back to save mode
    setCurrentSettingID(null); // Clear the current SettingID
    setErrors({});
  };

  const handleDeleteClick = async (settingID) => {
    Swal.fire({
      title: "Are you sure you want to delete this email setting?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post(
            `${BASE_URL}/api/Expense/DeleteEmailSTMP?SettingID=${settingID}`
          );
          Swal.fire(
            "Deleted!",
            "Your email setting has been deleted.",
            "success"
          );
          fetchEmailData(); // Fetch data again after deletion
        } catch (error) {
          console.error("Error deleting email setting:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "There was an error deleting the email setting. Please try again.",
          });
        }
      }
    });
  };

  return (
    <>
      <Navbar />
      <div className="cust-div min-h-content-minvh">
        <BlockUiOverlay visible={loading} />
        <div className="container mx-auto p-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">Email Sender Settings</h1>
            <div>
              <button
                className="bg-lightBlue-500 text-white active:bg-lightBlue-600 mr-3 text-xs font-bold uppercase px-4 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
                type="button"
                onClick={() => history.goBack()}
              >
                <i
                  className="fa fa-arrow-circle-left mr-1"
                  title="Go To All Message logs"
                ></i>
                Go Back
              </button>
            </div>
          </div>
          <div>
            <form onSubmit={handleSubmit} className="row">
              {/* First row: 3 fields */}

              <div className="form-group col-6 col-lg-4 mb-4">
                <input
                  type="email"
                  name="senderEmail"
                  placeholder="Sender Email"
                  value={formValues.senderEmail}
                  onChange={handleChange}
                  className="form-control custom-height w-full"
                />
                {errors.senderEmail && (
                  <p className="text-red-500 mt-1">{errors.senderEmail}</p>
                )}
              </div>
              <div className="form-group col-6 col-lg-4 mb-4">
                <input
                  type="text"
                  name="smtpUsername"
                  placeholder="SMTP Username"
                  value={formValues.smtpUsername}
                  onChange={handleChange}
                  className="form-control custom-height w-full"
                />
                {errors.smtpUsername && (
                  <p className="text-red-500 mt-1">{errors.smtpUsername}</p>
                )}
              </div>
              <div className="form-group col-6 col-lg-4 mb-4">
                <input
                  type="password"
                  name="smtpPassword"
                  placeholder="SMTP Password"
                  value={formValues.smtpPassword}
                  onChange={handleChange}
                  className="form-control custom-height w-full"
                />
                {errors.smtpPassword && (
                  <p className="text-red-500 mt-1">{errors.smtpPassword}</p>
                )}
              </div>
              {/* Second row: 2 fields */}
              <div className="form-group col-6 col-lg-4 mb-4">
                <input
                  type="text"
                  name="smtpHost"
                  placeholder="SMTP Host"
                  value={formValues.smtpHost}
                  onChange={handleChange}
                  className="form-control custom-height w-full"
                />
                {errors.smtpHost && (
                  <p className="text-red-500 mt-1">{errors.smtpHost}</p>
                )}
              </div>
              <div className="form-group col-6 col-lg-4 mb-4">
                <input
                  type="text"
                  name="smtpPort"
                  placeholder="SMTP Port"
                  value={formValues.smtpPort}
                  onChange={handleChange}
                  className="form-control custom-height w-full"
                />
                {errors.smtpPort && (
                  <p className="text-red-500 mt-1">{errors.smtpPort}</p>
                )}
              </div>

              <div className="form-group col-6 col-lg-4 mb-4 flex justify-end">
                {/* Buttons */}
                <button
                  type="submit"
                  className="bg-lightBlue-500 text-white active:bg-lightBlue-600 mr-3 text-xs font-bold uppercase px-4 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
                >
                  {isEditMode ? "Update Settings" : "Save Settings"}
                </button>
                <button
                  type="button"
                  onClick={handleClear}
                  className="bg-lightBlue-500 text-white active:bg-lightBlue-600 mr-3 text-xs font-bold uppercase px-4 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
                >
                  Clear
                </button>
              </div>
            </form>
          </div>
          <div className="overflow-x-auto mt-6">
            <table className="w-full min-w-full">
              <thead>
                <tr className="border-b bg-lightBlue-500 text-white">
                  <th className="cell py-2 px-2 font-bold">SMTP Username</th>
                  <th className="cell py-2 px-2 font-bold">SMTP Host</th>
                  <th className="cell py-2 px-2 font-bold">SMTP Port</th>

                  <th className="cell py-2 px-2 font-bold">Sender Email</th>
                  <th className="cell py-2 px-2 font-bold flex justify-end">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {emailData.length > 0 ? (
                  emailData.map((email) => (
                    <tr
                      key={email.settingID}
                      className="hover:bg-gray-100 border-b"
                    >
                      <td className="cell py-2 px-2">{email.smtpUsername}</td>
                      <td className="cell py-2 px-2">{email.smtpHost}</td>
                      <td className="cell py-2 px-2">{email.smtpPort}</td>

                      <td className="cell py-2 px-2">{email.senderEmail}</td>
                      <td className="cell py-2 px-2 flex justify-end ">
                        <img
                          title="Edit"
                          src={require("assets/img/Editicon.png")}
                          alt="Edit"
                          className="mr-2 cursor-pointer"
                          style={{ width: "20px" }}
                          onClick={() => handleEditSMTP(email.settingID)}
                        />
                        <img
                          src={require("assets/img/deleteicon.png")}
                          alt="Delete"
                          title="Delete"
                          className="cursor-pointer"
                          style={{ width: "20px" }}
                          onClick={() => handleDeleteClick(email.settingID)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-2 px-4 text-center">
                      No email settings found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default EmailServerSettings;
